import {
	IsBoolean,
	IsNotEmpty,
	IsUUID,
	IsString,
	IsArray,
	ValidateNested,
	IsOptional,
	IsNumber,
	IsInt,
	Min,
} from 'class-validator';
import { Type } from 'class-transformer';
import {
	UserDto,
	CreateMembershipDiscountDto,
	UpsertMembershipTierDto,
	MembershipTierDto,
	DiscountDto,
} from './';
import type { MembershipIntakeFormSettings } from './intake-form-settings.dto';
import { MembershipPublishingStateEnum } from '../../enums';

export class MembershipDto {
	public id: string;
	public mainCommunityId: string;
	public membershipChatCid?: string;
	public name: string;
	public description: string;
	public isPinned: boolean;
	public isUnlisted: boolean;
	public publishingState: MembershipPublishingStateEnum;
	public capacity: number | null;
	public createdAt: Date;
	public updatedAt: Date;
	public archivedAt?: Date;
	public deletedAt?: Date;

	public mainCommunity?: UserDto;
	public tiers?: MembershipTierDto[];
	public discounts?: DiscountDto[];

	public intakeFormSettings?: MembershipIntakeFormSettings;
	public activeMembersCount?: number;
	public revenue?: number;
}

export class UpsertMembershipDto {
	@IsBoolean()
	@IsNotEmpty()
	public isDraft: boolean;

	@IsUUID()
	@IsNotEmpty()
	public mainCommunityId: string;

	@IsString()
	@IsNotEmpty()
	public name: string;

	@IsString()
	@IsOptional()
	public description: string;

	@IsArray()
	@IsOptional()
	@Type(() => UpsertMembershipTierDto)
	@ValidateNested({ each: true })
	public tiers: UpsertMembershipTierDto[]; // + validate uniqueness + throw an error on update when tiers already exist

	@IsArray()
	@IsOptional()
	@Type(() => CreateMembershipDiscountDto)
	@ValidateNested({ each: true })
	public discounts?: CreateMembershipDiscountDto[]; // + validate uniqueness

	@IsBoolean()
	@IsOptional()
	public isPinned?: boolean;

	@IsBoolean()
	@IsOptional()
	public isUnlisted?: boolean;

	@IsNumber()
	@IsInt()
	@Min(0)
	@IsOptional()
	public capacity?: number | null;

	@IsOptional()
	public intakeFormSettings?: MembershipIntakeFormSettings;

	@IsBoolean()
	@IsOptional()
	public rewritePinnedMembership?: boolean; // 409 will be sent is there is another pinned value and the flag = false
}

export class MembershipStatsDto {
	revenue: {
		totalAmount: number;
		thisWeekAmount: number;
		thisDayAmount: number;
	};
	activeMembersCount: number;
}

export class MembershipsReservationDto {
	public tier: string | null;
	public userIpAndAgent: string;
	public reservedAt: number;
	public expiresAt: number;
}

export class MembershipTotalsDto {
	public membershipsCount: number;
	public membershipsLimit: number;
	public spotsCount: number | null;
}

export class MembershipTotalsWithReservationsDto extends MembershipTotalsDto {
	public membershipsCountWithReservations: number;
	public spotsCountWithReservations: number | null;
}

export class UpsertMembershipReservationDto {
	public tier: string | null;
}

export class MembershipsReservationCacheDataDto {
	public membership: MembershipDto;
	public reservations: MembershipsReservationDto[];
	public totals: MembershipTotalsDto;
}
